import { useEffect } from 'react';

// Global modal stack to track which modal should handle escape
let modalStack = [];

/**
 * Advanced Escape key hook with unsaved changes detection
 * 
 * @param {boolean} isOpen - Whether the modal/form is open
 * @param {function} onClose - Function to call when closing
 * @param {function} detectFormChanges - Function that returns boolean for unsaved changes
 * @param {string} confirmMessage - Message to show in confirmation dialog
 * @param {string} modalId - Unique identifier for this modal (defaults to auto-generated)
 */
export function useEscapeKeyAdvanced(
  isOpen,
  onClose,
  detectFormChanges,
  confirmMessage = 'You have unsaved changes. Are you sure you want to close?',
  modalId = `modal-${Math.random().toString(36).substr(2, 9)}`
) {
  useEffect(() => {
    if (!isOpen) return;
    
    // Add this modal to the stack
    modalStack.push(modalId);

    const handleEscapeKey = (event) => {
      if (event.key === 'Escape' && !event.defaultPrevented) {
        // Only handle escape if this is the topmost modal
        const isTopmost = modalStack[modalStack.length - 1] === modalId;
        
        if (!isTopmost) {
          return; // Let the topmost modal handle it
        }
        
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        
        // Check if there are unsaved changes
        const hasChanges = typeof detectFormChanges === 'function' ? detectFormChanges() : false;
        
        if (hasChanges && confirmMessage) {
          // Show confirmation dialog
          const shouldClose = window.confirm(confirmMessage);
          if (shouldClose) {
            onClose();
          }
        } else {
          // No changes or no confirmation needed, close immediately
          onClose();
        }
      }
    };

    // Nested widgets get first refusal (for example, closing a select menu).
    // The modal handles only an Escape that remains unconsumed.
    document.addEventListener('keydown', handleEscapeKey);

    // Cleanup
    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
      // Remove this modal from the stack
      modalStack = modalStack.filter(id => id !== modalId);
    };
  }, [isOpen, onClose, detectFormChanges, confirmMessage, modalId]);
}

// Simple version for basic use cases
export function useEscapeKey(
  isOpen,
  onClose,
  formData = {},
  originalData = {},
  hasUnsavedChanges = null,
  options = {}
) {
  const {
    confirmMessage = "You have unsaved changes. Are you sure you want to close without saving?",
    enabled = true
  } = options;

  const detectChanges = () => {
    if (hasUnsavedChanges !== null) {
      return hasUnsavedChanges;
    }
    return JSON.stringify(formData) !== JSON.stringify(originalData);
  };

  useEscapeKeyAdvanced(
    isOpen && enabled,
    onClose,
    detectChanges,
    confirmMessage
  );
}
